import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';

import type { Service, ValidationErrors } from 'webapp-types';
import { isAlive } from 'webapp/utils/common';

import type { SourceMigrationMap } from 'webapp/components/board/popup-migration-tool';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import FormSelectWithActionComponent from 'webapp/components/form/select-with-action';
import type { ActionOption } from 'webapp/components/form/select-with-action';
import RowErrorComponent from 'webapp/components/row-error';

import type { onConnectSourceResult } from 'webapp/services/connect';
import type { SourceListEntry } from 'webapp/services/model-manager/sources';

interface Args {
	sourceMigrationMap: SourceMigrationMap;
	datasourceList: SourceListEntry[];
	showSelectionErrors: boolean;
}

export default class BoardSourceMigrationRowComponent extends Component<Args> {
	@service('connect') declare connectService: Service.Connect;
	@service('model-manager/source-types')
	declare mmSourceTypesService: Service.ModelManager.SourceTypes;

	get showSelectionErrors(): boolean {
		return this.args.sourceMigrationMap.isError && this.args.showSelectionErrors;
	}

	get errors(): ValidationErrors<'source'> {
		const errors: ValidationErrors<'source'> = {};
		if (this.showSelectionErrors) {
			errors.source = [{ message: 'Required' }];
		}

		return errors;
	}

	get targetSourceIdString() {
		const id = this.args.sourceMigrationMap.targetSourceId;
		return id ? String(id) : null;
	}

	get sourceActions() {
		const ga4SourceType = this.mmSourceTypesService.findModelByKey('GoogleAnalytics4');
		return [
			{
				id: '',
				text: 'Connect another ' + ga4SourceType?.name,
				html: '<a class="source-add">Connect another ' + ga4SourceType?.name + '</a>',
				isSpecial: true,
				position: 'bottom',
				onClick: () => {
					const sourceType = ga4SourceType;

					if (sourceType) {
						void sourceType.connectNew().then((result: onConnectSourceResult) => {
							if (result.isSuccess && isAlive(this)) {
								const sourceInfo = result.source;
								this.changeSourceMigrationTarget(String(sourceInfo.id));
							}
						});
					}
				},
			} as ActionOption,
		];
	}

	@action
	changeSourceMigrationTarget(value: string | null) {
		this.args.sourceMigrationMap.targetSourceId = +value!;
	}
	<template>
		<div class="form__row">
			<hr />
			<div class="source">
				<img class="source__avatar" src={{@sourceMigrationMap.fromSource.avatar}} alt />
				<div class="source__info">
					<span class="source__name">{{@sourceMigrationMap.fromSource.label}}</span>
					<span class="source__creation">
						Created by
						{{@sourceMigrationMap.fromSource.createdByName}},
						{{@sourceMigrationMap.fromSource.createdOnRelative}}
					</span>
				</div>
			</div>
			<CommonUiIconComponent @name="ArrowRight" @size="sm" />
			<div>
				<FormSelectWithActionComponent
					name="datasource"
					@selected={{this.targetSourceIdString}}
					@options={{@datasourceList}}
					@actionOptions={{this.sourceActions}}
					@onChange={{this.changeSourceMigrationTarget}}
					@placeholder="Select or connect new GA4 data source"
					@isRequired={{true}}
					@isNumeric={{false}}
					@searchInLabel2={{true}}
				/>
				<RowErrorComponent @items={{this.errors.source}} />
			</div>
		</div>
	</template>
}
